import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { apiGet, apiPost } from "../../Utils/apiFetch";
import apiPath from "../../Utils/apiPath";

// ---- Fallback banners (used until the API responds / if it returns empty) ----
const FALLBACK_BANNERS = [
  "/assets/images/en-mobile-6a95364c9e043.webp",
  "/assets/images/en-mobile-67193b3d3b900.webp",

];

export default function Banner() {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(1); // matches original "active" second slide
  const [bannerData, setBannerData] = useState([]);
  const [messagelist, setMessageList] = useState([]);
  const swiperRef = useRef(null);
  const getData = async () => {
    const { status, data } = await apiPost(apiPath.getBanner, {
      type: "home_top",
    });
    if (status == 200) {
      if (data?.success) {
        setBannerData(data?.results?.banners || []);
      }
    }
  };
  const messageList = async () => {
    const { status, data: response_users } = await apiGet(apiPath.messageList);
    if (status === 200) {
      if (response_users.success) {
        setMessageList(response_users.results);
      }
    }
  };
  useEffect(() => {
    getData();
    messageList();
  }, []);

  // API banners resolve to full CDN urls; fall back to the static list if empty
  const banners =
    bannerData?.length > 0
      ? bannerData.map(
          (item) => process.env.REACT_APP_API_BASE_URL + item?.banner_path,
        )
      : [];

  useEffect(() => {
    const t = setTimeout(() => swiperRef.current?.update(), 150);
    return () => clearTimeout(t);
  }, [banners]);

  return (
    <div className="banner-content">
      {/* ---- Mobile banner (Swiper, same classNames as before) ---- */}
      <div className="row hidden-md-and-up mb-bg-color no-gutters">
        <div
          className="flicking flicking1 mt-3 mb-2 flicking-viewport"
          style={{
            touchAction: "pan-y",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
            userSelect: "none",
            WebkitUserDrag: "none",
          }}
        >
          <Swiper
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            key={bannerData?.length > 0 ? "api" : "fallback"}
            className="flicking-camera"
            slidesPerView={1.15}
            spaceBetween={8}
            centeredSlides={true}
            loop={banners.length > 2}
            observer={true}
            observeParents={true}
            initialSlide={activeIndex}
            onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
          >
            {banners.map((src, i) => (
              <SwiperSlide
                key={src + i}
                className={
                  "mx-1 flicking-panel mob_banner" +
                  (i === activeIndex ? " active" : "")
                }
              >
                <img
                  src={src}
                  width={320}
                  height={150}
                  className="winx9_panel"
                  style={{ width: "100%", height: "auto" }}
                />
              </SwiperSlide>
            ))}
          </Swiper>

          <div className="flicking-pagination flicking-pagination-bullets">
            {banners.map((_, i) => (
              <span
                key={i}
                className={
                  "paging-bullet" +
                  (i === activeIndex
                    ? " flicking-pagination-bullet-active"
                    : "")
                }
              >
                <i />
                <b />
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* ---- Desktop carousel (unchanged) ---- */}
      <div className="row hidden-sm-and-down no-gutters">
        <div className="col col-12">
          <div
            className="v-window home-banner-carousel hidden-sm-and-down v-item-group theme--dark v-carousel v-carousel--hide-delimiter-background"
            style={{ height: "auto" }}
          >
            <div
              className="v-window__container v-window__container--is-active"
              style={{ height: "auto" }}
            >
              {banners.map((src, i) => (
                <div
                  key={src + i}
                  className={
                    "v-window-item" +
                    (i === activeIndex
                      ? " v-window-x-transition-enter-active v-window-x-transition-enter-to"
                      : "")
                  }
                  style={i === activeIndex ? {} : { display: "none" }}
                >
                  <div
                    className="v-image v-responsive v-carousel__item theme--light"
                    style={{ height: "auto" }}
                  >
                    <div className="v-responsive__content">
                      <div className="v-image v-responsive theme--light">
                        <div
                          className="v-responsive__sizer"
                          style={{ paddingBottom: "24.3229%" }}
                        />
                        <div
                          className="v-image__image v-image__image--preload v-image__image--cover"
                          style={{
                            backgroundImage: `url("${src}")`,
                            backgroundPosition: "center center",
                          }}
                        />
                        <div className="v-responsive__content" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div
              className="v-carousel__controls"
              style={{ left: "auto", right: "auto" }}
            >
              <div className="v-item-group theme--dark">
                {banners.map((_, val) => (
                  <button
                    key={val}
                    type="button"
                    value={val}
                    onClick={() => setActiveIndex(val)}
                    className={
                      "v-carousel__controls__item v-btn v-btn--icon v-btn--round theme--dark v-size--small" +
                      (val === activeIndex
                        ? " v-item--active v-btn--active"
                        : "")
                    }
                    aria-label={t("CarouselSlideOf", {
                      current: val + 1,
                      total: banners.length,
                    })}
                  >
                    <span className="v-btn__content">
                      <i
                        aria-hidden="true"
                        className="v-icon notranslate mdi mdi-minus theme--dark"
                        style={{ fontSize: "18px" }}
                      />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Marquee (unchanged) ---- */}
      <div className="marquee-row">
        <div className="marquee-wrapper">
          <div className="marquee-icon" />
          <marquee className="marquee">
            {messagelist?.length > 0 &&
              messagelist?.map((res) => {
                return (
                  <span className="pr-6 mr-6">
                    <label className="d-inline-block home-announcment-content">
                      {res?.message}
                    </label>
                  </span>
                );
              })}
          </marquee>
        </div>
      </div>
    </div>
  );
}
